import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { DealCard } from '@/components/DealCard';
import { CodeRevealBox } from '@/components/CodeRevealBox';
import { Heart, MapPin, Clock, ExternalLink, Share2, Calendar, Tag, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import axios from 'axios';
import api from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DealDetailPage = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [deal, setDeal] = useState(null);
  const [similarDeals, setSimilarDeals] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDeal();
  }, [id]);

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/deals/${id}`);
      setDeal(response.data);
      setIsSaved(user?.saved_deals?.includes(response.data.id) || false);
      
      // Fetch similar deals
      const similarResponse = await axios.get(`${API}/deals?category=${response.data.category_id}&limit=4`);
      setSimilarDeals(similarResponse.data.filter(d => d.id !== id));
    } catch (error) {
      console.error('Error fetching deal:', error);
      toast({
        title: "Error",
        description: "Failed to load deal details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to save deals",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        await api.delete(`/users/${user.id}/save-deal/${id}`);
        setIsSaved(false);
        toast({ title: "Deal removed from saved" });
      } else {
        await api.post(`/users/${user.id}/save-deal/${id}`);
        setIsSaved(true);
        toast({ title: "Deal saved!" });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save deal",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGetDeal = async (e) => {
    // Don't prevent default - let the anchor tag open the link
    try {
      // Track click asynchronously
      api.post(`/deals/${id}/click`).catch(err => 
        console.error('Failed to track click:', err)
      );
    } catch (error) {
      console.error('Failed to track click:', error);
    }
    // Let the anchor tag handle the navigation to Amazon
  };

  const handleShare = async () => {
    const url = window.location.href;
    const text = `Check out this deal: ${deal.title}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: deal.title, text, url });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(url);
      toast({ title: "Link copied to clipboard!" });
    }
  };

  const getTimeRemaining = () => {
    if (!deal) return '';
    const now = new Date();
    const expires = new Date(deal.expires_at);
    const diff = expires - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} days left`;
    if (hours > 0) return `${hours} hours left`;
    return 'Ending soon';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg h-96 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Deal Not Found</h2>
            <p className="text-gray-600 mb-6">This deal may have expired or been removed</p>
            <Link to="/deals">
              <Button>Browse All Deals</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm">
            <Link to="/" className="text-emerald-600 hover:underline">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/deals" className="text-emerald-600 hover:underline">Deals</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-600">{deal.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Card */}
              <Card>
                <CardContent className="p-0">
                  <div className="relative">
                    <img
                      src={deal.image_url}
                      alt={deal.title}
                      className="w-full h-96 object-cover rounded-t-lg"
                    />
                    <div className="absolute top-4 right-4 flex gap-2">
                      <Badge className="bg-red-600 text-white font-bold text-lg">
                        {deal.discount_percentage}% OFF
                      </Badge>
                      {deal.is_featured && (
                        <Badge className="bg-yellow-500 text-black">
                          ⭐ Featured
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    <h1 className="text-3xl font-bold mb-2" data-testid="deal-detail-title">{deal.title}</h1>
                    <p className="text-lg text-gray-600 mb-4">{deal.brand_name}</p>
                    <p className="text-gray-700 leading-relaxed">{deal.description}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Code Reveal Box */}
              <CodeRevealBox dealId={deal.id} hasCode={deal.promo_code !== null && deal.promo_code !== undefined} />

              {/* Details */}
              <Card>
                <CardContent className="p-6 space-y-4">
                  <h2 className="text-xl font-semibold">Deal Details</h2>
                  <Separator />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Tag className="w-5 h-5" />
                      <span>{deal.category_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-5 h-5" />
                      <span>{deal.locations.join(', ')}</span>
                    </div>
                    
                    {/* Only show time remaining for non-property deals */}
                    {deal.category_name !== 'Property' && (
                      <>
                        <div className="flex items-center gap-2 text-orange-600">
                          <Clock className="w-5 h-5" />
                          <span>{getTimeRemaining()}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-5 h-5" />
                          <span>Valid until {new Date(deal.expires_at).toLocaleDateString()}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold mb-2">How to Redeem:</h3>
                    <p className="text-gray-600">
                      Click "Get Deal" button below to visit the merchant's website. Follow the instructions provided to redeem your discount.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Terms & Conditions:</h3>
                    <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                      <li>Valid for dates specified above</li>
                      <li>Subject to availability</li>
                      <li>Cannot be combined with other offers</li>
                      <li>One deal per person/transaction</li>
                      <li>Advance booking may be required</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Price Card */}
              <Card className="sticky top-4">
                <CardContent className="p-6 space-y-6">
                  <div>
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="text-4xl font-bold text-emerald-600">
                        AED {deal.discounted_price}
                      </span>
                      <span className="text-xl text-gray-500 line-through">
                        AED {deal.original_price}
                      </span>
                    </div>
                    <p className="text-green-600 font-semibold">
                      You Save: AED {(deal.original_price - deal.discounted_price).toFixed(2)}
                    </p>
                  </div>

                  <a
                    href={deal.deal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleGetDeal}
                    className="block w-full"
                    data-testid="get-deal-link"
                  >
                    <Button
                      size="lg"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="get-deal-button"
                    >
                      Get Deal <ExternalLink className="ml-2" size={18} />
                    </Button>
                  </a>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      variant="outline"
                      className="flex-1"
                      data-testid="save-deal-detail-button"
                    >
                      <Heart
                        className={`mr-2 ${isSaved ? 'fill-red-500 text-red-500' : ''}`}
                        size={18}
                      />
                      {isSaved ? 'Saved' : 'Save'}
                    </Button>
                    <Button onClick={handleShare} variant="outline" className="flex-1">
                      <Share2 className="mr-2" size={18} />
                      Share
                    </Button>
                  </div>

                  <Separator />

                  <div className="text-sm text-gray-600 space-y-2">
                    <div className="flex justify-between">
                      <span>Views:</span>
                      <span className="font-semibold">{deal.views}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saved by:</span>
                      <span className="font-semibold">{deal.saves} people</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Similar Deals */}
          {similarDeals.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold mb-6">Similar Deals</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {similarDeals.map((similarDeal) => (
                  <DealCard key={similarDeal.id} deal={similarDeal} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
